import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError } from "../api/errors";
import { productsApi } from "../api/products";
import type { ProductListItem } from "../api/types";
import { ErrorNotice, formatMoney } from "../components/ui";
import { ConfirmDialog } from "../products/ConfirmDialog";
import {
  discountLabel,
  formatMinorForInput,
  isDiscounted,
  parseMoneyToMinor,
} from "../products/money";

/**
 * One sale price, set or cleared, through the product domain that owns it.
 *
 * THIS DIALOG OWNS NOTHING. It asks `ProductService` for the draft and then
 * patches one field of it. There is no discount endpoint, no discount table and
 * no second price model — the sale price is a column on the revision being
 * worked on, and this is a small screen over the same route the product
 * workspace uses.
 *
 * WHY IT ALWAYS OPENS A DRAFT FIRST
 * `POST /drafts` returns the EXISTING draft when there is one and creates one
 * only when there is not (`publish_service.create_draft_revision`), so this is
 * safe to repeat and cannot produce a second draft. It is also the only way to
 * learn the draft revision's own version, which is the concurrency token the
 * PATCH requires — the list row carries the PRODUCT's version, which is a
 * different number for a different purpose.
 *
 * WHAT IT DELIBERATELY CANNOT DO
 * Change the base price, the currency, the name, the family or the lifecycle:
 * those belong to Product Management, and a discount screen that could reach
 * them would be a second product editor. And it never publishes — the new
 * price is a draft until somebody takes the normal publish decision.
 */
export function SetDiscountDialog({
  item,
  onClose,
}: {
  item: ProductListItem;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const alreadyDiscounted = isDiscounted(item.base_price, item.sale_price);

  const [text, setText] = useState(
    alreadyDiscounted ? formatMinorForInput(item.sale_price) : "",
  );

  const save = useMutation({
    mutationFn: async (saleMinor: number | null) => {
      // Returns the open draft if the product has one; creates it otherwise.
      const opened = await productsApi.openDraft(item.product_id);
      return productsApi.updateDraft(item.product_id, {
        expected_version: opened.draft_revision.version,
        // The ONLY field sent. `exclude_unset` on the backend means everything
        // else the draft holds is untouched, so this cannot quietly rewrite a
        // name or a base price somebody else is editing.
        sale_price: saleMinor,
      });
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["products"] });
      onClose();
    },
    onError: async (error) => {
      // A conflict means the draft moved under this dialog. Reload the list so
      // the next attempt carries a token the server will accept — and never
      // resend: what to do about somebody else's change is the operator's call.
      if (error instanceof ApiError && error.isStale) {
        await client.invalidateQueries({ queryKey: ["products"] });
      }
    },
  });

  const trimmed = text.trim();
  const parsed = trimmed === "" ? null : parseMoneyToMinor(trimmed);
  const base = item.base_price;

  const malformed = trimmed !== "" && parsed === null;
  // A discount is a reduction. The backend accepts a sale price equal to the
  // base — the schema allows it — but nothing is discounted by one, so this
  // screen will not create one. The server stays the final authority.
  const notAReduction =
    parsed !== null && base !== null && !isDiscounted(base, parsed);

  const previewLabel =
    parsed !== null && base !== null ? discountLabel(base, parsed) : null;

  const valid = !malformed && !notAReduction && parsed !== null;

  return (
    <ConfirmDialog
      title={alreadyDiscounted ? "Change discount" : "Set discount"}
      confirmLabel={alreadyDiscounted ? "Save discount" : "Set discount"}
      busy={save.isPending}
      confirmDisabled={!valid || save.isPending}
      onCancel={onClose}
      onConfirm={() => {
        if (!valid) return;
        save.mutate(parsed);
      }}
    >
      <div className="stack" data-testid="discount-dialog">
        <dl className="discounts__facts">
          <dt>Product</dt>
          <dd>{item.name}</dd>
          <dt>Base price</dt>
          <dd data-testid="dialog-base">
            {base === null || item.currency === null
              ? "—"
              : formatMoney(base, item.currency)}
          </dd>
          <dt>Currency</dt>
          <dd>{item.currency ?? "—"}</dd>
        </dl>

        <div className="discounts__field">
          <label className="discounts__label" htmlFor="discount-sale-price">
            Sale price
          </label>
          <input
            id="discount-sale-price"
            className="num"
            type="text"
            inputMode="decimal"
            style={{ width: 160 }}
            value={text}
            autoFocus
            placeholder="e.g. 340.00"
            onChange={(event) => setText(event.target.value)}
          />
          <p className="discounts__hint" data-testid="discount-preview">
            {previewLabel === null
              ? "Enter a price below the base price."
              : `Customers save ${previewLabel}`}
          </p>
          {malformed ? (
            <p className="field-error">Enter an amount such as 340.00</p>
          ) : null}
          {notAReduction ? (
            <p className="field-error">
              A sale price has to be below the base price.
            </p>
          ) : null}
        </div>

        {/* Said before the operator commits, not after: the shopper is not
            affected by this until somebody publishes it. */}
        <p className="muted">
          This changes the working draft. Customers keep seeing the current
          price until the product is published from Product Management.
        </p>

        {save.error ? <ErrorNotice error={save.error} /> : null}
      </div>
    </ConfirmDialog>
  );
}

/**
 * Clearing the sale price, which is the whole of what removing a discount is.
 *
 * `sale_price: null` and nothing else. NEVER `sale_price = base_price`: null is
 * the authoritative "not discounted" state in the schema, in the catalogue and
 * in the order path, and an equal price would leave a row that looks like a
 * sale to anything reading the column.
 */
export function RemoveDiscountDialog({
  item,
  onClose,
}: {
  item: ProductListItem;
  onClose: () => void;
}) {
  const client = useQueryClient();

  const remove = useMutation({
    mutationFn: async () => {
      const opened = await productsApi.openDraft(item.product_id);
      return productsApi.updateDraft(item.product_id, {
        expected_version: opened.draft_revision.version,
        sale_price: null,
      });
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["products"] });
      onClose();
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.isStale) {
        await client.invalidateQueries({ queryKey: ["products"] });
      }
    },
  });

  return (
    <ConfirmDialog
      title="Remove discount"
      confirmLabel="Remove discount"
      busy={remove.isPending}
      onCancel={onClose}
      onConfirm={() => remove.mutate()}
    >
      <div className="stack" data-testid="remove-discount-dialog">
        <p>
          <strong>{item.name}</strong> goes back to its base price
          {item.base_price !== null && item.currency !== null
            ? ` of ${formatMoney(item.base_price, item.currency)}`
            : ""}
          .
        </p>
        <p className="muted">
          This changes the working draft. Customers keep seeing the current
          price until the product is published from Product Management.
        </p>
        {remove.error ? <ErrorNotice error={remove.error} /> : null}
      </div>
    </ConfirmDialog>
  );
}
